<template>
  <div id="app">
    <a class="skip-link" href="#work">{{ $t("a11y.skipToWork") }}</a>
    <router-view />
  </div>
</template>

<script>
export default {
  name: "App",
};
</script>

<style>
/* =============================================================
   Reset
   ============================================================= */
*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* =============================================================
   Tokens — WARE GROOTTE
   The record is set at measurement scale. The work is shown at 1:1.
   ============================================================= */
:root {
  /* ── Colour ─────────────────────────────────────────────── */
  --ground: #f2efea;
  --ground-sunk: #e9e4db; /* recessed bands, press band */
  --ink: #16130f;
  --text: #34302a;
  --grey: #6b645b;
  --quiet: #9a9187;
  --hairline: rgba(22, 19, 15, 0.11);
  --hairline-strong: rgba(22, 19, 15, 0.18);
  --accent: #b4522d;
  --accent-light: #e08a5c; /* accent on night */
  --amber: #d9a066;
  --accent-soft: rgba(180, 82, 45, 0.12);
  --night: #14110e;
  --night-soft: #1c1815;
  --on-night: #f4f1ec;
  --on-night-2: #cfc8bf;
  --on-night-3: #948c82;

  /* Aliases — kept so nothing breaks mid-build */
  --color-primary: var(--accent);
  --color-primary-light: rgba(180, 82, 45, 0.1);
  --color-primary-medium: rgba(180, 82, 45, 0.5);
  --color-secondary: rgb(233, 228, 219);
  --color-white: #ffffff;
  --color-text-dark: var(--ink);
  --color-text-gray: var(--grey);
  --color-text-light: var(--quiet);
  --color-night: var(--night);
  --color-night-soft: #1c1815;

  /* ── Type ───────────────────────────────────────────────── */
  --font-sans: "Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  --font-measure: "IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace;
  --font-serif: var(--font-sans);
  --font-script: var(--font-sans);

  --t-display: clamp(40px, 7.4vw, 92px); /* cold-open name */
  --t-h1: clamp(30px, 4.2vw, 52px); /* stage title */
  --t-h2: clamp(26px, 3vw, 40px); /* section head */
  --t-h3: 22px;
  --t-lead: 19px;
  --t-body: 17px;
  --t-sm: 14px;
  --t-xs: 13px;
  --m-value: 13px; /* Plex Mono, tabular */
  --m-label: 10.5px; /* Plex Mono, 0.16em, uppercase */

  --ls-display: -0.032em;
  --ls-h: -0.026em;
  --ls-title: -0.014em;
  --ls-label: 0.16em;

  /* ── Space (8px base) ───────────────────────────────────── */
  --s-1: 4px;
  --s-2: 8px;
  --s-3: 12px;
  --s-4: 16px;
  --s-5: 24px;
  --s-6: 32px;
  --s-7: 48px;
  --s-8: 64px;
  --s-9: 96px;
  --s-10: 128px;
  --gutter: 24px;
  --container: 1320px;
  --edge: 32px; /* container side padding */

  /* ── Radii ──────────────────────────────────────────────── */
  --r-sm: 8px;
  --r-md: 14px;
  --r-lg: 22px;
  --r-xl: 28px;
  --r-pill: 999px;
  --r-tile: 22.37%; /* iOS squircle approximation for square marks */
  --radius-sm: var(--r-sm);
  --radius-md: var(--r-md);
  --radius-lg: var(--r-lg);
  --radius-xl: var(--r-xl);
  --radius-pill: var(--r-pill);

  /* ── Light & shadow ─────────────────────────────────────── */
  /* One declared light origin. Every shadow below was authored from it.
     A new shadow MUST be derived from it: y-offset positive, x-offset 0,
     blur roughly 2.4x y. No runtime, no light bus. */
  --light-origin: 50% -18%;

  --sh-1: 0 1px 2px rgba(22, 19, 15, 0.05), 0 2px 6px rgba(22, 19, 15, 0.05);
  --sh-2: 0 2px 6px rgba(22, 19, 15, 0.06), 0 10px 30px rgba(22, 19, 15, 0.09);
  --sh-3: 0 4px 12px rgba(22, 19, 15, 0.07), 0 24px 60px rgba(22, 19, 15, 0.13);
  --sh-plate: 0 6px 16px rgba(20, 17, 14, 0.1), 0 30px 70px rgba(20, 17, 14, 0.22);
  --sh-night: 0 10px 40px rgba(20, 17, 14, 0.28);
  --shadow-sm: var(--sh-1);
  --shadow-md: var(--sh-2);
  --shadow-lg: var(--sh-3);

  --glass-fill: linear-gradient(
    126deg,
    rgba(255, 253, 249, 0.80) 0%,
    rgba(252, 248, 242, 0.58) 55%,
    rgba(255, 253, 249, 0.68) 100%
  );
  --glass-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95),
    0 0 0 0.5px rgba(22, 19, 15, 0.05), 0 0 0 1px rgba(255, 255, 255, 0.4),
    0 10px 30px rgba(22, 19, 15, 0.09);

  /* ── Motion ─────────────────────────────────────────────── */
  --ease-out: cubic-bezier(0.22, 1, 0.36, 1); /* entrances, expansion */
  --ease-inout: cubic-bezier(0.65, 0, 0.35, 1); /* state swaps */
  --ease-soft: cubic-bezier(0.2, 0.8, 0.2, 1); /* buttons */

  --d-1: 140ms;
  --d-2: 220ms;
  --d-3: 320ms;
  --d-4: 420ms;
  --d-5: 620ms;

  --travel: 220px; /* the single parallax multiplier */
  --depth-scale: 1; /* -> 0.5 below 900px */
  --fold: 620px; /* cold-open scrub distance */

  /* Grain, painted into the ground rather than blended over the page */
  --noise: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.28 0 0 0 0 0.22 0 0 0 0 0.14 0 0 0 0.16 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

@media (max-width: 900px) {
  :root {
    --depth-scale: 0.5;
  }
}
@media (max-width: 720px) {
  :root {
    --edge: 20px;
  }
}

/* Hairlines: 0.5px on retina, 1px below. Never in between. */
:root {
  --hair-w: 1px;
}
@media (min-resolution: 2dppx) {
  :root {
    --hair-w: 0.5px;
  }
}

/* =============================================================
   Base
   ============================================================= */
html {
  scroll-behavior: smooth;
  background: var(--ground);
}
html.has-panel {
  overflow: hidden;
}

/* The ambient field. Two fixed radial tints plus the grain, painted as
   background layers on the body — zero elements, zero per-frame cost.
   This replaces four blurred .orb divs that ran a permanent rAF loop. */
body {
  font-family: var(--font-sans);
  color: var(--text);
  line-height: 1.6;
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: var(--ground);
  background-image: var(--noise),
    radial-gradient(
      52% 34% at 12% 8%,
      rgba(196, 150, 110, 0.20),
      transparent 70%
    ),
    radial-gradient(
      46% 30% at 92% 34%,
      rgba(180, 82, 45, 0.10),
      transparent 70%
    );
  background-size: 200px 200px, 100% 100%, 100% 100%;
  background-repeat: repeat, no-repeat, no-repeat;
  background-attachment: scroll, fixed, fixed;
}

img {
  display: block;
  max-width: 100%;
}
button {
  font-family: inherit;
  cursor: pointer;
  border: 0;
  background: none;
  color: inherit;
}
a {
  color: inherit;
  text-decoration: none;
}

::selection {
  background: var(--accent);
  color: #fff;
}

#app {
  position: relative;
  min-height: 100vh;
}

/* =============================================================
   Focus & skip link — every interactive element keeps a ring
   ============================================================= */
:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 3px;
}
.on-night :focus-visible {
  outline-color: var(--accent-light);
}

.skip-link {
  position: absolute;
  left: var(--s-4);
  top: var(--s-4);
  z-index: 500;
  padding: 10px 18px;
  border-radius: var(--r-pill);
  background: var(--ink);
  color: var(--on-night);
  font-size: var(--t-sm);
  font-weight: 600;
  transform: translateY(-200%);
  transition: transform var(--d-2) var(--ease-out);
}
.skip-link:focus {
  transform: none;
}

/* =============================================================
   Layout primitives
   ============================================================= */
.container {
  max-width: var(--container);
  margin: 0 auto;
  padding: 0 var(--edge);
  position: relative;
}

main,
nav,
footer,
section {
  position: relative;
  z-index: 1;
}

/* Glass — three named singletons only (nav, readout strip, mobile bar).
   Everything that repeats uses the alpha-panel recipe instead. */
.glass {
  background-image: var(--glass-fill);
  box-shadow: var(--glass-shadow);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
}

.rule {
  border: 0;
  border-top: var(--hair-w) solid var(--hairline);
}

/* Measurement type */
.num {
  font-family: var(--font-measure);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}
.label {
  font-family: var(--font-measure);
  font-size: var(--m-label);
  font-weight: 500;
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  color: var(--quiet);
}

.h-section {
  font-size: var(--t-h2);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: var(--ls-h);
  color: var(--ink);
}

/* Section numeral, sitting far back in the field */
.ghost {
  position: absolute;
  top: -0.32em;
  right: 0;
  font-size: 128px;
  font-weight: 200;
  line-height: 1;
  color: rgba(22, 19, 15, 0.05);
  pointer-events: none;
  user-select: none;
}

/* =============================================================
   Reveal — index-driven, so a 17-row ledger can stagger
   ============================================================= */
.reveal {
  opacity: 0;
  transform: translateY(14px);
  transition: opacity var(--d-4) var(--ease-out),
    transform var(--d-4) var(--ease-out);
  transition-delay: calc(var(--reveal-i, 0) * 30ms);
}
.reveal.in {
  opacity: 1;
  transform: none;
}

/* =============================================================
   Buttons
   ============================================================= */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 52px;
  padding: 0 26px;
  border-radius: var(--r-pill);
  font-weight: 600;
  font-size: 15px;
  transition: transform var(--d-2) var(--ease-soft),
    box-shadow var(--d-2) ease, background var(--d-1) ease,
    color var(--d-1) ease;
}
.btn-primary {
  background: var(--ink);
  color: #fff;
  box-shadow: var(--sh-2);
}
.btn-primary:hover {
  transform: translateY(-2px);
  background: var(--accent);
  box-shadow: var(--sh-3);
}
.btn-quiet {
  background: transparent;
  color: var(--ink);
  box-shadow: inset 0 0 0 var(--hair-w) var(--hairline-strong);
}
.btn-quiet:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.6);
}
.btn-arrow {
  width: 30px;
  height: 30px;
  border-radius: var(--r-pill);
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  transition: transform var(--d-2) var(--ease-soft);
  flex-shrink: 0;
}
.btn-quiet .btn-arrow {
  background: var(--accent-soft);
  color: var(--accent);
}
.btn:hover .btn-arrow {
  transform: translate(3px, -3px);
}
.btn svg {
  width: 16px;
  height: 16px;
}

/* =============================================================
   Scrollbar
   ============================================================= */
::-webkit-scrollbar {
  width: 10px;
}
::-webkit-scrollbar-track {
  background: var(--ground-sunk);
}
::-webkit-scrollbar-thumb {
  background: rgba(22, 19, 15, 0.22);
  border-radius: 5px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--accent);
}

/* =============================================================
   Reduced motion — the depth loop never starts (handled in JS);
   this is the belt-and-braces CSS clamp.
   ============================================================= */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
  .allow-fade {
    transition-duration: 200ms !important;
  }
  .reveal {
    opacity: 1;
    transform: none;
  }
}
</style>
